import { useState, useRef, useEffect } from "react";
import * as XLSX from "xlsx";
import {
  UploadCloud,
  FileSpreadsheet,
  X,
  UploadCloudIcon,
  EyeIcon,
  Download,
  Link,
  Info,
  Copy,
  InfoIcon,
} from "lucide-react";
import { useService } from "../../context/ServiceContext";
import Header from "../../Components/Header";
import Seo from "../../Components/SeoComponent/Seo";
import { GstBulkUploading } from "../../Services/ApiTestService/apiTestService";
import { fetchProfile } from "../../Services/profile/profileservice";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";

export default function GstBulkUpload() {
  const [file, setFile] = useState(null);
  const [data, setData] = useState([]);
  const [previewClicked, setPreviewClicked] = useState();
  const [profileData, setProfileData] = useState(null);
  const [isLoading, setIsLoading] = useState();
  const [isUpLoading, setIsUpLoading] = useState();
  const [uploadResponse, setUploadResponse] = useState(null);
  const [referenceId, setReferenceId] = useState(null);

  const inputRef = useRef(null);

  const { serviceName } = useService();

  //   import * as XLSX from "xlsx";

  const parseAndValidateExcel = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const workbook = XLSX.read(e.target.result, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];

        const jsonData = XLSX.utils.sheet_to_json(sheet, {
          defval: "",
        });

        if (!jsonData.length) {
          reject("Excel file is empty");
          return;
        }

        // 🔹 Validate header
        const headers = Object.keys(jsonData[0]).map((h) =>
          h.trim().toLowerCase(),
        );

        if (!headers.includes("gstin")) {
          reject("Excel must contain a 'gstin' column");
          return;
        }

        // 🔹 Validate row count
        if (jsonData.length > 200) {
          reject("Maximum 200 rows allowed");
          return;
        }

        resolve(jsonData);
      };

      reader.onerror = () => reject("Failed to read file");
      reader.readAsBinaryString(file);
    });

  const handleFile = (uploadedFile) => {
    if (!uploadedFile) return;

    setFile(uploadedFile);

    const reader = new FileReader();
    reader.onload = (e) => {
      const workbook = XLSX.read(e.target.result, { type: "binary" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const parsedData = XLSX.utils.sheet_to_json(sheet, { defval: "" });
      setData(parsedData);
    };
    reader.readAsBinaryString(uploadedFile);
  };

  const handleUpload = async () => {
    if (!file) return;
    setIsUpLoading(true);

    try {
      const excelData = await parseAndValidateExcel(file);

      const formData = new FormData();
      formData.append("file", file);
      formData.append("api_id", profileData.user_id);
      formData.append("api_key", profileData.api_key);
      formData.append("token_id", profileData.token_id);

      const res = await GstBulkUploading(formData);

      if (res?.data) {
        setUploadResponse(res);
        //  console.log(res);
      }

      if (res?.status?.code === 200) {
        showSuccessToast(res?.message);
      }

      if (res?.data?.status?.code === 422) {
        showErrorToast(res?.data?.message);
      }

      if (res?.data?.status?.code === 500) {
        showErrorToast(res?.data?.message);
      }
    } catch (err) {
      alert(err);
      showErrorToast(err);
    } finally {
      setIsUpLoading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files[0]);
  };

  const removeFile = () => {
    setFile(null);
    setData([]);
  };

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        const res = await fetchProfile();
        setProfileData(res?.data || null);
      } catch (error) {
        console.error(error);
        showErrorToast("Failed to fetch profile");
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  return (
    <>
      <Seo />
      <Header heading="Bulk GST To Contact Fetch" />
      <div className="w-full">
        <div className="max-w-5xl mx-auto p-6 rounded-2xl bg-yellow-50 mt-0 text-yellow-700">
          <h1 className="text-2xl font-semibold mb-4">
            Gst To Contact Bulk Upload
          </h1>

          {/* Upload Box */}
          {!file && (
            <div
              onDrop={onDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => inputRef.current.click()}
              className="border-2 border-dashed border-gray-300 rounded-xl p-10
          text-center cursor-pointer hover:border-blue-400 transition"
            >
              <UploadCloud className="mx-auto h-10 w-10 text-gray-400 mb-3" />
              <p className="text-sm text-gray-600">
                Drag & drop your Excel file here or
                <span className="text-blue-600 font-medium">
                  {" "}
                  click to upload
                </span>
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Supported formats: .xls, .xlsx
              </p>

              <input
                ref={inputRef}
                type="file"
                accept=".xls,.xlsx"
                className="hidden"
                onChange={(e) => handleFile(e.target.files[0])}
              />
            </div>
          )}

          {/* File Preview Header */}
          {file && (
            <div className="flex items-center justify-between bg-gray-50 border rounded-lg p-4 mt-4">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="text-yellow-600" />
                <span className="text-sm font-medium">{file.name}</span>
              </div>
              <div className="flex items-center gap-4 md:gap-8">
                <button
                  onClick={() => setPreviewClicked(!previewClicked)}
                  className="text-yellow-500 hover:text-gray-600"
                >
                  <EyeIcon size={18} />
                </button>

                <button
                  onClick={removeFile}
                  className="text-red-500 hover:text-red-600"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
          )}
          <div className="flex justify-between px-4 items-center flex-wrap">
            {file && (
              <div
                className={`flex w-fit items-center justify-between  ${isUpLoading ? "bg-blue-500" : "bg-yellow-600"} hover:bg-slate-800 border rounded-lg p-4 mt-4`}
              >
                <button
                  onClick={handleUpload}
                  disabled={isUpLoading}
                  className="text-white flex items-center gap-2 font-semibold text-xl hover:text-white disabled:opacity-65"
                >
                  <UploadCloudIcon
                    className={`text-white ${isUpLoading ? "animate-bounce" : "animate-pulse"}`}
                  />
                  {isUpLoading ? "Uploading" : "Upload"}
                  <span className="flex items-center gap-2">
                    <span className="text-sm font-medium">{file.name}</span>
                  </span>
                </button>
              </div>
            )}

            {file && data && (
              <div className="flex gap-2 px-4 items-center">
                <div className="text-xl font-bold ">Total Row Count:</div>

                <p>{data?.length}</p>
              </div>
            )}

            <a
              href="/sample gstin file.xlsx"
              download
              className="flex gap-2 items-center py-4 cursor-pointer text-yellow-600 hover:underline"
            >
              <Download />
              Download Sample File
            </a>

            <div className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 border border-blue-200 px-3 py-2 rounded-md">
              <Info className="w-4 h-4" />
              <span>Max 200 records allowed in a single File</span>
            </div>
          </div>

          {uploadResponse?.status?.code === 200 && (
            <div className="flex flex-col items-center gap-4 p-5 my-4 rounded-xl border border-yellow-200 bg-yellow-50 text-center">
              <p className="text-sm font-semibold text-yellow-700">
                Uploaded Successfully
              </p>

              <div className="flex items-center gap-2 px-3 py-2 bg-white border rounded-lg text-xs text-gray-700">
                <span className="truncate max-w-[200px]" title={referenceId}>
                  {referenceId}
                </span>

                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      uploadResponse?.data?.reference_id,
                    );
                    showSuccessToast("Reference ID copied");
                  }}
                  className="text-yellow-600 flex items-center gap-2  hover:text-yellow-700"
                >
                  Copy Reference Id <Copy />
                </button>
              </div>

              <a
                href="/BulkReportAll"
                className="text-lg font-medium text-blue-600 hover:underline"
              >
                View & download your report
              </a>
            </div>
          )}

          {data.length > 10 && (
            <div>
              <p className="text-sm text-gray-500 mt-4 p-3">
                Find the first 10 records of your file
              </p>
              <p className="text-sm text-gray-500 mt-4 p-3">
                You can add max 200 Records
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-center w-full mt-4">
                  <div className="flex flex-wrap justify-center p-4 rounded-lg md:rounded-full border gap-2 border-green-400 font-semibold bg-green-50 text-green-700 ">
            <InfoIcon className="ml-4"/>
            file processing will take 5 - 10 minutes or depends on the number of
            records, processed file report can be download from <a className="underline text-blue-600 font-bold" href="/BulkReportAll"> Report Page</a>
          </div>
        </div>
      </div>
    </>
  );
}
